// Shared helpers for the Fleet desk module faces (FleetModulesRow1-3.tsx).
import { useQueries, useQuery } from '@tanstack/react-query'
import { accountApi, systemApi } from '../../api/client'
import type { BenchmarkReturns } from '../../api/client'
import { spyDayEndDate } from '../../lib/spyVerdict'
import type { Book, BookRole } from '../../types'

export { bookCountMarker, num } from './fleetSummaries'

export interface FleetModuleProps {
  books: Book[]
  booksPending: boolean // useBooks().isLoading
  booksError: boolean // useBooks().isError
}

export const DASH = '—'
export const POSITION_CAP = 25
// One constant for both the runs fetch and the pip strip (ruling 10).
export const RUNS_LIMIT = 10

interface QueryState {
  isLoading: boolean
  isPaused: boolean
  data: unknown
}

/**
 * Loading rule (plan Review Focus 2, see useBooks.ts): in flight, or paused
 * before any data arrived. Either way the face is LOADING, never a resolved
 * zero.
 */
export const stillLoading = (q: QueryState) => q.isLoading || (q.isPaused && q.data === undefined)

/** Failed with nothing to show: the book's figure is UNKNOWN, not zero. */
export const unavailable = (q: { isError: boolean; data: unknown }) => q.isError && q.data === undefined

/**
 * Dormant = registered but not in engine/status books[] (BookCard
 * convention). Its endpoints 404 by design, so it is never queried for fleet
 * totals and contributes nothing. Every other book that fails is
 * "unavailable" and must be surfaced, never silently dropped.
 */
export const isDormant = (b: Book) => b.bookStatus === null
export const liveBooks = (books: Book[]) => books.filter((b) => !isDormant(b))

export const ROLE_INITIAL: Record<BookRole, string> = {
  research: 'R',
  validation: 'V',
  live: 'L',
  book: 'B',
}

export const signTone = (v: number): 'gain' | 'loss' | 'neutral' => {
  const r = Math.round(v)
  return r > 0 ? 'gain' : r < 0 ? 'loss' : 'neutral'
}

/**
 * Per-book `['account', id]` queries for the NON-dormant books, in `books`
 * order (same key/options as BookCard so the drawer is a cache hit). A book
 * keeps its last-good data through a transient refetch failure; a book that
 * failed with no data is counted in `unavailable`.
 */
export function useBookAccounts(books: Book[]) {
  const live = liveBooks(books)
  const results = useQueries({
    queries: live.map((b) => ({
      queryKey: ['account', b.id],
      queryFn: () => accountApi.get(b.id),
      retry: false,
      refetchInterval: 10000,
    })),
  })
  const resolved = live.flatMap((book, i) => {
    const account = results[i]?.data
    return account ? [{ book, account }] : []
  })
  return {
    live,
    loading: results.some(stillLoading),
    unavailable: results.filter(unavailable).length,
    resolved,
  }
}

/**
 * PicksPanel's honesty caveats (PicksPanel.tsx:92-107, 117-121), shared by
 * the picks faces so they survive the move off the panel (spec §3):
 *  - `spyDayStale`: the SPY `day` window often ends on the PRIOR session
 *    during market hours (spyVerdict.ts IMPORTANT 4); show its end date.
 *  - `lastSession`: the market is not open, so "today" is the last session.
 *    Same `['system-health']` key/queryFn as PicksPanel and AppShell; unknown
 *    or loading health reads as open.
 */
export function usePicksCaveats(bench: BenchmarkReturns | undefined) {
  const healthQ = useQuery({
    queryKey: ['system-health'],
    queryFn: () => systemApi.getHealth(),
    refetchInterval: 60000,
  })
  const marketStatus = healthQ.data?.market_status
  const marketClosed = marketStatus !== undefined && marketStatus !== 'open' && marketStatus !== 'unknown'
  const spyDayEnd = spyDayEndDate(bench)
  const spyDayStale = spyDayEnd !== null && spyDayEnd !== new Date().toLocaleDateString('en-CA')
  return { marketClosed, spyDayEnd: spyDayStale ? spyDayEnd : null }
}
